import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogBackdrop,
} from '@headlessui/react';
import { useState, useEffect } from 'react';
import { Button } from 'src/components/button';
import FormInput from 'src/components/form-input';
import { CloseIon } from 'src/components/icons/CloseIcon';
import { useGlobalContext } from 'src/context/global/use-global-context';
import { useUndoRedo } from 'src/context/undo-redo';
import { twMerge } from 'tailwind-merge';

export default function DialogProjectData() {
  const { showDialogs, setShowDialogs, projectData, setProjectData } =
    useGlobalContext();
  const { takeSnapshot } = useUndoRedo();

  const [formData, setFormData] = useState({
    projectName: projectData?.projectName || '',
    companyName: projectData?.companyName || '',
    companyAddress: projectData?.companyAddress || '',
    drawnBy: projectData?.drawnBy || '',
    revisionNumber: projectData?.revisionNumber || '',
    projectNumber: projectData?.projectNumber || '',
    responsibleEngineer: projectData?.responsibleEngineer || '',
    description: projectData?.description || '',
  });

  useEffect(() => {
    if (showDialogs.projectData) {
      setFormData({
        projectName: projectData?.projectName || '',
        companyName: projectData?.companyName || '',
        companyAddress: projectData?.companyAddress || '',
        drawnBy: projectData?.drawnBy || '',
        revisionNumber: projectData?.revisionNumber || '',
        projectNumber: projectData?.projectNumber || '',
        responsibleEngineer: projectData?.responsibleEngineer || '',
        description: projectData?.description || '',
      });
    }
  }, [showDialogs.projectData, projectData]);

  const onCancel = () => {
    setShowDialogs({ projectData: false });
  };

  const onSave = () => {
    takeSnapshot();
    setProjectData({
      projectName: formData.projectName,
      companyName: formData.companyName,
      companyAddress: formData.companyAddress,
      drawnBy: formData.drawnBy,
      revisionNumber: formData.revisionNumber,
      projectNumber: formData.projectNumber,
      responsibleEngineer: formData.responsibleEngineer,
      description: formData.description,
    });
    setShowDialogs({ projectData: false });
  };

  if (!showDialogs.projectData) return null;

  return (
    <Dialog
      open
      onClose={onCancel}
      className="relative z-50"
      data-testid="dialog-project-data"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className={twMerge([
              'relative transform overflow-hidden rounded-lg bg-white px-2 pb-2 pt-3 text-left shadow-xl transition-all sm:my-8 sm:p-6',
              'data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95',
              'w-full max-w-2xl',
            ])}
            data-testid="dialog-project-data-panel"
          >
            <DialogTitle
              as="h3"
              className="pb-4 text-xl font-bold"
              data-testid="dialog-project-data-title"
            >
              Project Information
            </DialogTitle>
            <div className="absolute right-4 top-4">
              <button
                type="button"
                onClick={onCancel}
                data-testid="dialog-project-data-close-button"
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <CloseIon className="h-6 w-6" />
              </button>
            </div>

            <div className="mt-4 space-y-4">
              <FormInput
                id="project-name"
                name="projectName"
                label="Project name"
                value={formData.projectName}
                onChange={(e) =>
                  setFormData({ ...formData, projectName: e.target.value })
                }
                data-testid="dialog-project-data-project-name-input"
                wrapperClassName="w-full"
              />

              <FormInput
                id="company-name"
                name="companyName"
                label="Company Name"
                value={formData.companyName}
                onChange={(e) =>
                  setFormData({ ...formData, companyName: e.target.value })
                }
                data-testid="dialog-project-data-company-name-input"
                wrapperClassName="w-full"
              />

              <FormInput
                id="company-address"
                name="companyAddress"
                label="Company Address"
                value={formData.companyAddress}
                onChange={(e) =>
                  setFormData({ ...formData, companyAddress: e.target.value })
                }
                data-testid="dialog-project-data-company-address-input"
                wrapperClassName="w-full"
              />

              <FormInput
                id="drawn-by"
                name="drawnBy"
                label="Drawn by"
                value={formData.drawnBy}
                onChange={(e) =>
                  setFormData({ ...formData, drawnBy: e.target.value })
                }
                data-testid="dialog-project-data-drawn-by-input"
                wrapperClassName="w-full"
              />

              <FormInput
                id="revision-number"
                name="revisionNumber"
                label="Revision"
                value={formData.revisionNumber}
                onChange={(e) =>
                  setFormData({ ...formData, revisionNumber: e.target.value })
                }
                data-testid="dialog-project-data-revision-number-input"
                wrapperClassName="w-full"
              />

              <FormInput
                id="project-number"
                name="projectNumber"
                label="Project Number"
                value={formData.projectNumber}
                onChange={(e) =>
                  setFormData({ ...formData, projectNumber: e.target.value })
                }
                data-testid="dialog-project-data-project-number-input"
                wrapperClassName="w-full"
              />

              <FormInput
                id="responsible-engineer"
                name="responsibleEngineer"
                label="Responsible Engineer"
                value={formData.responsibleEngineer}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    responsibleEngineer: e.target.value,
                  })
                }
                data-testid="dialog-project-data-responsible-engineer-input"
                wrapperClassName="w-full"
              />

              <FormInput
                id="description"
                name="description"
                label="Description"
                isTextarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                data-testid="dialog-project-data-description-input"
                wrapperClassName="w-full"
                className="min-h-32 resize-y"
              />
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <Button
                type="button"
                onClick={onCancel}
                data-testid="dialog-project-data-cancel-button"
                color="gray"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={onSave}
                data-testid="dialog-project-data-save-button"
              >
                Save
              </Button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
